import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  ExternalLink,
  Eye,
  Loader2,
  Search,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { Calendar as CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Table, TableBody, TableCell,
  TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { cn, getInitials, downloadCSV } from "@/lib/utils";
import { showSuccessToast, showErrorToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { useEmployeeHours } from "@/hooks/useStats";
import { useOvertime } from "@/hooks/useOvertime";
import type { OvertimeEmployee } from "@/services/overtime.service";
import { EmployeeOvertimeDialog, type OvertimeDialogEmployee } from "@/components/planning/EmployeeOvertimeDialog";

// ─── Types ────────────────────────────────────────────────────────────────────

type Period = "week" | "month";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatHours(minutes: number): string {
  if (minutes <= 0) return "0h 00m";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

// ─── Composants réutilisables ─────────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: string | number;
  description: string;
  icon: React.ElementType;
  highlight?: "red" | "amber";
}

function StatCard({ title, value, description, icon: Icon, highlight }: StatCardProps) {
  return (
    <Card className={highlight === "red" ? "border-red-200" : highlight === "amber" ? "border-amber-200" : ""}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={`h-4 w-4 ${highlight === "red" ? "text-red-500" : highlight === "amber" ? "text-amber-500" : "text-muted-foreground"}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${highlight === "red" ? "text-red-600" : highlight === "amber" ? "text-amber-600" : ""}`}>
          {value}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </CardContent>
    </Card>
  );
}

// Badge coloré pour le statut dépassement
function StatutBadge({ status, label }: {
  status: OvertimeEmployee["status"];
  label: string;
}) {
  if (status === "over") return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      <TrendingUp className="h-3 w-3" />
      {label}
    </span>
  );
  if (status === "under") return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
      <TrendingDown className="h-3 w-3" />
      {label}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
      ✓ Exact
    </span>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export const ReportsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [period, setPeriod] = useState<Period>("month");
  const [refDate, setRefDate] = useState<Date>(new Date());
  const [search, setSearch] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // ── Modal dépassements par jour ───────────────────────────────────────────
  const [overtimeModalEmployee, setOvertimeModalEmployee] =
    useState<OvertimeDialogEmployee | null>(null);

  // ── Données ──────────────────────────────────────────────────────────────
  const { data: hoursData, isLoading: hoursLoading } = useEmployeeHours(period, refDate);
  const { data: overtimeData, isLoading: overtimeLoading } = useOvertime(period, refDate);

  const loading = hoursLoading || overtimeLoading;
  const label = hoursData?.label ?? "—";

  // Croise les deux sources par employee.id pour avoir toutes les colonnes
  const rows = useMemo(() => {
    const hoursMap = new Map(
      (hoursData?.employees ?? []).map((e) => [e.employee.id, e])
    );
    return (overtimeData?.employees ?? []).map((emp) => ({
      ...emp,
      sessionsCount: hoursMap.get(emp.employee.id)?.sessionsCount ?? 0,
    }));
  }, [hoursData, overtimeData]);

  const filteredRows = search.trim()
    ? rows.filter((r) => r.employee.name.toLowerCase().includes(search.toLowerCase()))
    : rows;

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalMinutes  = hoursData?.totalMinutes ?? 0;
  const activeWorkers = (hoursData?.employees ?? []).filter((e) => e.minutes > 0).length;
  const nbOver        = rows.filter((e) => e.status === "over").length;
  const nbUnder       = rows.filter((e) => e.status === "under").length;

  // ── Navigation ────────────────────────────────────────────────────────────
  const handlePrev = () => {
    const d = new Date(refDate);
    if (period === "week")  d.setDate(d.getDate() - 7);
    if (period === "month") d.setMonth(d.getMonth() - 1);
    setRefDate(d);
  };
  const handleNext = () => {
    const d = new Date(refDate);
    if (period === "week")  d.setDate(d.getDate() + 7);
    if (period === "month") d.setMonth(d.getMonth() + 1);
    setRefDate(d);
  };

  // ── Export CSV ────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (rows.length === 0) { showErrorToast("Aucune donnée à exporter"); return; }
    setIsExporting(true);
    try {
      const data = rows.map((r) => ({
        "Employé":  r.employee.name,
        "Sessions": r.sessionsCount,
        "Planifié": r.plannedMinutes > 0 ? r.plannedFormatted : "—",
        "Réel":     r.workedMinutes > 0  ? r.workedFormatted  : "—",
        "Statut":   r.status === "over" ? `Dépassement ${r.overtimeFormatted}` : r.status === "under" ? `Sous planning ${r.overtimeFormatted}` : "Exact",
      }));
      const today = format(new Date(), "yyyy-MM-dd");
      const companyName = user?.company?.name?.replace(/\s+/g, "_") || "entreprise";
      downloadCSV(data, `rapports_${companyName}_${today}.csv`);
      showSuccessToast("Export réussi");
    } catch {
      showErrorToast("Erreur lors de l'export");
    } finally {
      setIsExporting(false);
    }
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-primary" />
            Rapports horaires
          </h1>
          <p className="text-sm text-muted-foreground">
            Planning vs heures réelles par employé — {label}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={isExporting || rows.length === 0}
          className="gap-2 w-full sm:w-auto"
        >
          {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Exporter CSV
        </Button>
      </div>

      {/* Onglets période + navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <TabsList>
            <TabsTrigger value="week">Semaine</TabsTrigger>
            <TabsTrigger value="month">Mois</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePrev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="min-w-[220px] justify-start capitalize">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {label}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={refDate}
                onSelect={(d) => d && setRefDate(d)}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="icon" onClick={handleNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" onClick={() => setRefDate(new Date())}>
            Aujourd'hui
          </Button>
        </div>
      </div>

      {/* Cartes de stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          title="Total heures"
          value={formatHours(totalMinutes)}
          icon={Clock}
          description={label}
        />
        <StatCard
          title="Employés actifs"
          value={activeWorkers}
          icon={Users}
          description={`sur ${rows.length} employé(s)`}
        />
        <StatCard
          title="Dépassements"
          value={nbOver}
          icon={TrendingUp}
          description="au-dessus du planning"
          highlight={nbOver > 0 ? "red" : undefined}
        />
        <StatCard
          title="Sous le planning"
          value={nbUnder}
          icon={TrendingDown}
          description="en-dessous du planning"
          highlight={nbUnder > 0 ? "amber" : undefined}
        />
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Rechercher un employé..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tableau unifié */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow className="[&_th]:h-auto [&_th]:py-4">
              <TableHead>Employé</TableHead>
              <TableHead className="text-center">Sessions</TableHead>
              <TableHead className="text-center">Planifié</TableHead>
              <TableHead className="text-center">Travailé</TableHead>
              <TableHead className="text-center">Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading ? (
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  <TableCell className="py-4">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  </TableCell>
                  {[...Array(5)].map((_, j) => (
                    <TableCell key={j} className="py-4 text-center">
                      <Skeleton className="h-4 w-16 mx-auto" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  {search.trim() ? `Aucun résultat pour "${search}"` : "Aucun employé."}
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row) => {
                const [firstName, ...rest] = row.employee.name.split(" ");
                const lastName = rest.join(" ");
                return (
                  <TableRow
                    key={row.employee.id}
                    className={`transition-colors hover:bg-muted/50 ${row.status === "over" ? "bg-red-50/30" : ""}`}
                  >
                    <TableCell className="py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                          <span className="font-medium text-primary text-sm">
                            {getInitials(firstName, lastName)}
                          </span>
                        </div>
                        <span
                          className="font-medium cursor-pointer hover:underline"
                          onClick={() => navigate(`/users/${row.employee.id}`)}
                        >
                          {row.employee.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-4 text-center text-muted-foreground tabular-nums">
                      {row.sessionsCount > 0 ? row.sessionsCount : "—"}
                    </TableCell>
                    <TableCell className="py-4 text-center tabular-nums text-muted-foreground">
                      {row.plannedMinutes > 0 ? row.plannedFormatted : "—"}
                    </TableCell>
                    <TableCell className="py-4 text-center tabular-nums font-medium">
                      {row.workedMinutes > 0 ? row.workedFormatted : "—"}
                    </TableCell>
                    <TableCell className="py-4 text-center">
                      <StatutBadge status={row.status} label={row.overtimeMinutes === 0 ? "Exact" : row.overtimeFormatted} />
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Bouton détail dépassements (uniquement si over) */}
                        {row.status === "over" && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            title="Voir les dépassements par jour"
                            className="border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 shadow-sm"
                            onClick={() =>
                              setOvertimeModalEmployee({ id: row.employee.id, name: row.employee.name })
                            }
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {/* Lien profil */}
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          title="Voir le profil"
                          className="border-border/80 bg-muted/50 text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
                          onClick={() => navigate(`/users/${row.employee.id}`)}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modal dépassements par jour */}
      <EmployeeOvertimeDialog
        employee={overtimeModalEmployee}
        open={!!overtimeModalEmployee}
        onOpenChange={(open: boolean) => { if (!open) setOvertimeModalEmployee(null); }}
        from={overtimeData?.start ? format(new Date(overtimeData.start), "yyyy-MM-dd") : format(refDate, "yyyy-MM-dd")}
        to={overtimeData?.end ? format(new Date(overtimeData.end), "yyyy-MM-dd") : format(refDate, "yyyy-MM-dd")}
      />
    </div>
  );
};
